import { useEffect } from 'react';
import { useRouter } from '@framework/router';
import { useBreadcrumbs } from '@/creditflowresearch/ui/common/hooks';
import Flex from '@creditflowresearch/uilib/Flex';
import DealTileWithFallback from '@/creditflowresearch/ui/components/deals/DealTiles/DealTileWithFallback';
import CorporateTopCompanies from '@/creditflowresearch/ui/components/TopCompanies/TopCompaniesCorporate';
import CorporateMarketChart from '@/creditflowresearch/ui/components/CorporateMarketChart';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import { ArticlesTilesWithFallback } from '@/creditflowresearch/ui/components/creditFlow/articles/ArticlesTiles';
import StatsTileWithFallback from '@/creditflowresearch/ui/components/creditFlow/Stats/StatsTile/StatsTileWithFallback';
import { getMarketsBreadcrumbs } from '@/creditflowresearch/ui/components/profile/helpers/getMarketsBreadcrumbs';
import getProfileOverviewSchema from '@/creditflowresearch/ui/components/profile/getProfileMetaSchema';
import useHeaderMenuData from '@/creditflowresearch/application/HeaderMenu/useHeaderMenuData';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { RoadshowsTileWithFallback } from '@/creditflowresearch/ui/components/Roadshows/Tile';
import { useEmMarketFilters } from '@/creditflowresearch/application/PageFilters/hooks/useEmMarketFilters';
import { useSetMetaTags } from '@/creditflowresearch/application/actions/profile/hooks/useSetMetaTags';
import EmDealByMarketTile
  from '@/creditflowresearch/ui/components/deals/DealTiles/CorporateDealByMarketTile/EmDealByMarketTile';
import { useFeatureToggleContext } from '@framework/ui/Organisms/FeatureToggle/Context';
import { HIDE_TOP_COMPANIES } from '@creditflowresearch/config/featureToggle';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import styles from '@/creditflowresearch/ui/components/profile/profile.module.scss';
import marketColors from '@/creditflowresearch/ui/common/styles/marketColors';

const EmMarketProfile = () => {
  const { isHeaderInitialized } = useHeaderMenuData();
  const { asPath } = useRouter();
  const {
    checkAccessByUserRegions,
    checkAccessByUserProducts,
    checkAccessByUserMarket,
  } = usePageFiltersContext();
  const { checkAccessByUserPlan } = useUserPlanContext();
  const filterSets = useEmMarketFilters();
  const setMetaTags = useSetMetaTags();
  const { getFeatureByName } = useFeatureToggleContext();
  const { isEnabled: showTopCompanies } = getFeatureByName(HIDE_TOP_COMPANIES);
  const granted = useAccessGrant();

  const userAccessChecks = [
    checkAccessByUserRegions(),
    checkAccessByUserProducts(),
    checkAccessByUserMarket(),
  ];

  useBreadcrumbs(
    getMarketsBreadcrumbs,
    [isHeaderInitialized],
  );

  useEffect(() => {
    if (isHeaderInitialized) {
      setMetaTags({
        pathname: asPath,
      });
    }
  }, [isHeaderInitialized, asPath, setMetaTags]);

  return (
    <>
      <MetaSchema schema={ getProfileOverviewSchema(asPath) } />
      <div className={ styles.profileLayout }>
        <ProductPageHeader
          title="EM Market Overview"
          items={ filterSets }
          panelBackground={ marketColors.gradientMarketEm }
          accessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
          blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        />

        <ArticlesTilesWithFallback userAccessChecks={ userAccessChecks } />
        <RoadshowsTileWithFallback userAccessChecks={ userAccessChecks } />
        <DealTileWithFallback userAccessChecks={ userAccessChecks }>
          <EmDealByMarketTile />
        </DealTileWithFallback>
        { showTopCompanies ? (
          <Flex direction={ { sm: 'column', md: 'column', xl: 'row' } }>
            <CorporateTopCompanies className={ styles.topParentsWrapper } />
            <CorporateMarketChart className={ styles.chartWrapper } />
          </Flex>
        ) : (
          <CorporateMarketChart className={ styles.chartWrapperFullWidth } />
        ) }
        <StatsTileWithFallback userAccessChecks={ userAccessChecks } />
      </div>
      <MissingSomethingModalButton />
    </>
  );
};

export default EmMarketProfile;
